import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';
import { apiGateway } from '../../../../config';
import { CANCEL_STATUS, ORDER_TYPES } from 'src/constants';

import type { AxiosResponse } from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse } from '../../../../@types/common';
import type { Country, Factory, Item, MailTarget, Port } from '../../../../@types/common';
import type { ModalMode, EditData, Order, OrderSet, CreatedOrder, OrderHeader, OrderState, OrderStatus, CancelStatus, SendBackParams } from '../../../../@types/order';

// ----------------------------------------------------------------------

const initialState: OrderState = {
  isLoading: false,
  hasError: false,
  hasSearched: false,
  message: undefined,
  isModalOpen: false,
  modalMode: undefined,
  options: {
    shippingSchedule: undefined,
    country: undefined,
    factory: undefined,
    item: undefined,
    mailTarget: undefined,
    sourcePort: undefined,
    destinationPort: undefined,
  },
  orders: undefined,
  order: undefined,
  orderPrev: undefined,
  editData: {},
  dateFrom: DateTime.now().startOf('month').minus({ months: 2 }).toISO(),
  dateTo: DateTime.now().endOf('month').plus({ months: 12 }).toISO(),
};

const slice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    startLoading(state: OrderState) {
      state.isLoading = true;
    },

    hasError(state: OrderState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = true;
      state.message = action.payload;
    },

    resetMessage(state: OrderState) {
      state.isLoading = false;
      state.hasError = false;
      state.message = '';
    },

    setModalMode(state: OrderState, action: PayloadAction<ModalMode | undefined>) {
      state.modalMode = action.payload;
    },

    openModal(state: OrderState, action: PayloadAction<boolean>) {
      state.isModalOpen = action.payload;
    },

// ----------------------------------------------------------------------

    hasSearched(state: OrderState, action: PayloadAction<boolean>) {
      state.hasSearched = action.payload;
    },

    changeStartDate(state: OrderState, action: PayloadAction<string | undefined>) {
      state.dateFrom = action.payload;
    },

    changeEndDate(state: OrderState, action: PayloadAction<string | undefined>) {
      state.dateTo = action.payload;
    },

// ----------------------------------------------------------------------

    changeOrderType(state: OrderState, action: PayloadAction<string>) {
      state.order = state.order ? Object.assign(state.order, { orderType: action.payload }) : undefined;
      state.editData = Object.assign(state.editData, { orderType: action.payload });
    },

    changeOrderYear(state: OrderState, action: PayloadAction<string>) {
      const yearMonth = state.order && state.order.orderYearMonth ?
        DateTime.fromISO(state.order.orderYearMonth).set({ year: Number(action.payload) }).toUTC().toString()
        :
        DateTime.fromJSDate(new Date()).set({ year: Number(action.payload) }).toUTC().toString();
      state.order = state.order ? Object.assign(state.order, { orderYearMonth: yearMonth }) : undefined;
      state.editData = Object.assign(state.editData, { orderYearMonth: yearMonth });
    },

    changeOrderMonth(state: OrderState, action: PayloadAction<string>) {
      const yearMonth = state.order && state.order.orderYearMonth ?
        DateTime.fromISO(state.order.orderYearMonth).set({ month: Number(action.payload) }).toUTC().toString()
        :
        DateTime.fromJSDate(new Date()).set({ month: Number(action.payload) }).toUTC().toString();
      state.order = state.order ? Object.assign(state.order, { orderYearMonth: yearMonth }) : undefined;
      state.editData = Object.assign(state.editData, { orderYearMonth: yearMonth });
    },

    changeCustomerComment(state: OrderState, action: PayloadAction<string>) {
      state.order = state.order ? Object.assign(state.order, { customerComment: action.payload }) : undefined;
      state.editData = Object.assign(state.editData, { customerComment: action.payload });
    },

    changeTtcComment(state: OrderState, action: PayloadAction<string>) {
      state.order = state.order ? Object.assign(state.order, { ttcComment: action.payload }) : undefined;
      state.editData = Object.assign(state.editData, { ttcComment: action.payload });
    },

    changeSentBackReason(state: OrderState, action: PayloadAction<string>) {
      state.order = state.order ? Object.assign(state.order, { sendbackReason: action.payload }) : undefined;
      // 差し戻し理由だけはOrderとシェイプが異なる
      state.editData = Object.assign(state.editData, { orderSendback: { sendbackReason: action.payload } });
    },

// ----------------------------------------------------------------------

    createNewOrder(state: OrderState) {
      const newOrder: CreatedOrder = {
        uuid: uuid(),
        orderYearMonth: DateTime.fromJSDate(new Date()).toUTC().toString(),
        orderType: ORDER_TYPES.REGULAR,
        status: 'ORDER_PREPARING',
        customerComment: '',
        cancelStatus: CANCEL_STATUS.NULL,
        orderDetail: new Array(100).fill(null).map(e => ({ uuid: uuid() })),
      };
      state.order = newOrder;
      state.orderPrev = undefined;
      state.editData = newOrder;
    },
// ----------------------------------------------------------------------


    getOptionsCountrySuccess(state: OrderState, action: PayloadAction<Country[]>) {
      state.isLoading = false;
      state.options.country = action.payload;
    },

    getOptionsFactorySuccess(state: OrderState, action: PayloadAction<Factory[]>) {
      state.isLoading = false;
      state.options.factory = action.payload;
    },

    getOptionsItemSuccess(state: OrderState, action: PayloadAction<Item[]>) {
      state.isLoading = false;
      state.options.item = action.payload;
    },

    getOptionsMailTargetSuccess(state: OrderState, action: PayloadAction<MailTarget[]>) {
      state.isLoading = false;
      state.options.mailTarget = action.payload;
    },

    getOptionsPortSuccess(state: OrderState, action: PayloadAction<Port[]>) {
      state.isLoading = false;
      const allPorts: Port[] = action.payload;
      state.options.sourcePort = allPorts.filter(port => port.isSourcePort === true).filter(port => port.code !== 'KZU');
      state.options.destinationPort = allPorts.filter(port => port.isDestinationPort === true);
    },

// ----------------------------------------------------------------------

    getOrdersSuccess(state: OrderState, action: PayloadAction<OrderHeader[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.orders = action.payload;
    },

    getOrderSuccess(state: OrderState, action: PayloadAction<OrderSet>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.order = action.payload.order;
      state.orderPrev = action.payload.orderPrev;
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.order.id});
    },

    createOrderSuccess(state: OrderState, action: PayloadAction<Order>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully created';
      state.order = action.payload;
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.id});
      // オーダー一覧を更新する
      const { orderDetail, ...rest} = action.payload;
      const orderHeader = rest;
      if(state.orders) {
        state.orders = [orderHeader, ...state.orders];
      } else {
        state.orders = [orderHeader];
      }
    },

    updateOrderSuccess(state: OrderState, action: PayloadAction<OrderSet>) {
      // state.message以外はgetDeliverySuccessと同じ
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully updated';
      state.order = action.payload.order;
      state.orderPrev = action.payload.orderPrev;
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.order.id});
      // オーダー一覧を更新する
      const { orderDetail, ...rest} = action.payload.order;
      const orderHeader = rest;
      if(state.orders) {
        state.orders = state.orders.map((_orderHeader) => {
          if (_orderHeader.id === orderHeader.id) {
            return orderHeader;
          } else {
            return _orderHeader;
          }
        });
      } else {
        state.orders = [orderHeader];
      }
    },

    sendBackOrderSuccess(state: OrderState, action: PayloadAction<OrderHeader>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully send back';
      state.order = state.order ? Object.assign(state.order, { ...action.payload }) : undefined;
      // editDataをリセットする
      state.editData = Object.assign({}, {id: action.payload.id});
      // オーダー一覧を更新する
      const orderHeader = action.payload;
      if(state.orders) {
        state.orders = state.orders.map((_orderHeader) => {
          if (_orderHeader.id === orderHeader.id) {
            return orderHeader;
          } else {
            return _orderHeader;
          }
        });
      } else {
        state.orders = [orderHeader];
      }
    },

    approveCancelSuccess(state: OrderState, action: PayloadAction<Order[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
    },

    deleteOrderSuccess(state: OrderState, action: PayloadAction<Order[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
    },

  }
});

export default slice.reducer;


// TODO: https://stackoverflow.com/questions/63439021/handling-errors-with-redux-toolkit

// ----------------------------------------------------------------------

export function setModalMode(mode?: ModalMode): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.setModalMode(mode));
  }
};

export function openModal(open: boolean): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.openModal(open));
  }
};

export function hasError(message: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  }
};

export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  }
};

// ----------------------------------------------------------------------

export function changeStartDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeStartDate(date));
  }
};

export function changeEndDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeEndDate(date));
  }
};

// ----------------------------------------------------------------------

export function changeOrderType(type: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeOrderType(type));
  }
};

export function changeOrderYear(year: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeOrderYear(year));
  }
};

export function changeOrderMonth(month: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeOrderMonth(month));
  }
};

export function changeCustomerComment(comment: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeCustomerComment(comment));
  }
};

export function changeTtcComment(comment: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeTtcComment(comment));
  }
};

export function changeSentBackReason(reason: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeSentBackReason(reason));
  }
};

export function createNewOrder(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.createNewOrder());
  }
};

// ----------------------------------------------------------------------

export function getOptionsCountry(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Country[]>(
        apiGateway + '/options/country',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsCountrySuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsFactory(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Factory[]>(
        apiGateway + '/options/factory',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsFactorySuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsItem(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Item[]>(
        apiGateway + '/options/item',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsItemSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsMailTarget(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<MailTarget[]>(
        apiGateway + '/options/mailTarget',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsMailTargetSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsPort(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Port[]>(
        apiGateway + '/options/port',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsPortSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

// ----------------------------------------------------------------------

export function getOrders(dateFrom: string | undefined, dateTo: string | undefined): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<OrderHeader[]>(
        apiGateway + '/order/search',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.hasSearched(true));
      dispatch(slice.actions.getOrdersSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOrder(id: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<OrderSet>(
        apiGateway + '/order/' + id,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOrderSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function createOrder2(requestParams: EditData, nextStatus?: OrderStatus, sentbackFlag?: boolean): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    let params: EditData = requestParams;
    // 必要に応じてstatusを設定
    if(nextStatus){
      params.status = nextStatus;
    }
    // console.log(params);
    // リクエスト
    const response: AxiosResponse = await axios.post<Order>(
      apiGateway + '/order/withDetail',
      params,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
        },
        timeout: 60000
      }
    );

    dispatch(slice.actions.createOrderSuccess(response.data));
  };
};

export function updateOrder2(requestParams: EditData, nextStatus?: OrderStatus, sentbackFlag?: boolean): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    let params: EditData = requestParams;
    // 必要に応じてstatusを設定
    if(nextStatus){
      params.status = nextStatus;
    }
    // 必要に応じて差し戻しフラグを設定
    if(typeof sentbackFlag === 'boolean'){
      params.isSentback = sentbackFlag;
    }
    // console.log(params);
    // リクエスト
    const response: AxiosResponse = await axios.put<OrderSet>(
      apiGateway + '/order/withDetail/' + params.id,
      params,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
        },
        timeout: 60000
      }
    );

    dispatch(slice.actions.updateOrderSuccess(response.data));
  };
};

export function deleteOrder(): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const id: number | undefined = (getState().order.order as Order)?.id;
    if(id) {
      // リクエスト
      const response: AxiosResponse = await axios.delete<Order>(
        apiGateway + '/order/orderAndDelivery/' + id,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.deleteOrderSuccess(response.data));
    }
  };
};

// ----------------------------------------------------------------------

export function sendBackOrder(id: number, reason: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    
    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const params: SendBackParams = {
      status: 'SHIWO_TTC_ORDER_CONFIRMED',
      isSentback: true,
      orderSendback: {
        sendbackReason: reason,
      }
    };
    // console.log('dispatch send back: ', params);
    // リクエスト
    const response: AxiosResponse = await axios.put<OrderHeader[]>(
      apiGateway + '/order/sendback/' + id,
      params,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
        },
        timeout: 60000
      }
    );
    dispatch(slice.actions.sendBackOrderSuccess(response.data[0]));
  };
};

export function cancelOrder(nextStatus: CancelStatus): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const id: number | undefined = (getState().order.order as Order)?.id;
    if(id) {
      const params = {
        cancelStatus: nextStatus,
      };
      // console.log(params);
      // リクエスト
      const response: AxiosResponse = await axios.put<OrderSet>(
        apiGateway + '/order/withDetail/' + id,
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.updateOrderSuccess(response.data));
    } else {
      dispatch(slice.actions.hasError('No data to cancel'));
    }
  };
};

export function approveCancel(ids: number[]): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const id: number | undefined = (getState().order.order as Order)?.id;
    if(id) {
      // 承認パラメータを設定する
      const params = {
        cancelStatus: CANCEL_STATUS.CANCELLED,
        mailTarget: ids,
      };
      // console.log(params);
      // リクエスト
      const response: AxiosResponse = await axios.put<Order>(
        apiGateway + '/order/cancelApproveRequest/' + id,
        params,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.approveCancelSuccess(response.data));
    } else {
      dispatch(slice.actions.hasError('No data to approve'));
    }
  };
};

// ----------------------------------------------------------------------
